import { useGet } from "@albertine/api-client";
import type {
    PublicProposalBooking,
    RequestProposalResponseData,
} from "@albertine/types/openapi/lmt";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

export type TargetedPublicBooking = {
    booking: PublicProposalBooking;
    index: number;
};

type WithProposalProps = {
    requestId: string;
    targetBookingId: string | undefined;
    renderLoading?: React.JSX.Element;
    renderOnError: React.JSX.Element;
    children: (
        publicProposal: RequestProposalResponseData,
        targetedPublicBooking: TargetedPublicBooking | undefined,
    ) => React.JSX.Element;
};

function WithProposal(props: WithProposalProps) {
    const { targetBookingId, requestId, children, renderLoading, renderOnError } =
        props;

    const {
        data: publicProposal,
        error,
        isError,
        isLoading,
    } = useGet(
        "requestProposal",
        { param: { id: requestId } },
        { enabled: Boolean(requestId), retry: false },
    );

    useEffect(() => {
        if (!isError || !error) {
            return;
        }

        Sentry.captureException(error, {
            extra: {
                message: `WithProposal: requestProposal with id that does not exist ${requestId}`,
            },
        });
    }, [error, isError, requestId]);

    if (isError) {
        return renderOnError;
    }

    if (isLoading || !publicProposal) {
        return renderLoading || <LoadingSpinner />;
    }

    const targetPublicBooking = publicProposal.bookings
        .map((booking, index) => ({
            booking,
            index,
        }))
        .find((item) => item.booking.id === targetBookingId);

    return children(publicProposal, targetPublicBooking);
}

export default WithProposal;
